<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { Button } from 'frappe-ui'
import BrandLogo from '../components/BrandLogo.vue'
import { hasAnyRole, roleHomeRoute, session } from '../session'

const route = useRoute()

const signedIn = computed(() => !!session.boot?.authenticated)

/**
 * Customers go to their appointments. Any other signed-in role goes to that
 * role's home: the customer portal would refuse them. The label stays
 * "My bookings" as the public nav's signed-in entry.
 */
const accountRoute = computed(() => {
  if (session.boot && hasAnyRole(['Customer'])) return '/portal/appointments'
  if (session.boot) return roleHomeRoute(session.boot)
  return '/portal/appointments'
})

const signInRoute = computed(() => ({
  path: '/login',
  query: { redirect: route.fullPath },
}))
</script>

<template>
  <div class="flex min-h-screen flex-col bg-surface-base text-ink-gray-9">
    <header
      class="sticky top-0 z-30 border-b border-outline-gray-2 bg-surface-elevation-1"
    >
      <div
        class="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-2 gap-y-1 px-4 py-3"
      >
        <router-link to="/salons" class="flex items-center gap-2 text-lg font-semibold text-ink-gray-9">
          <BrandLogo :size="28" />
          Glamora
        </router-link>
        <nav class="flex flex-wrap items-center gap-1" aria-label="Marketplace">
          <Button
            variant="ghost"
            theme="gray"
            label="Find a salon"
            route="/salons"
          />
          <Button
            variant="ghost"
            theme="gray"
            label="Compare"
            route="/salons/compare"
          />
          <Button
            variant="ghost"
            theme="gray"
            label="List your salon"
            route="/list-your-salon"
          />
        </nav>
        <div class="flex flex-wrap items-center gap-2 sm:ml-auto">
          <Button
            v-if="signedIn"
            variant="solid"
            label="My bookings"
            :route="accountRoute"
          />
          <template v-else>
            <Button
              variant="ghost"
              theme="gray"
              label="Sign in"
              :route="signInRoute"
            />
            <Button variant="solid" label="Create account" route="/signup" />
          </template>
        </div>
      </div>
    </header>

    <main class="mx-auto w-full min-w-0 max-w-6xl flex-1 px-4 py-6">
      <slot />
    </main>

    <footer class="border-t border-outline-gray-2 px-4 py-4">
      <p class="mx-auto max-w-6xl text-sm text-ink-gray-5">
        Glamora — find a salon and book a visit.
      </p>
    </footer>
  </div>
</template>
